/**
 * SignUpPage - High-quality landing page for AMS signup
 * Showcases AMS features and benefits with integrated signup form
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/lib/supabase";
import { 
  Zap, 
  Users, 
  Calendar, 
  BarChart3, 
  Mail, 
  CheckCircle2, 
  Target, 
  TrendingUp,
  Briefcase,
  FileText,
  Shield,
  Loader2,
  AlertCircle
} from "lucide-react";
import { motion } from "framer-motion";
import { ShootingStars } from "@/components/ui/shooting-stars";
import { StarsBackground } from "@/components/ui/stars-background";

interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  companyName: string;
  agreeToTerms: boolean;
}

export default function SignUpPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    companyName: "",
    agreeToTerms: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const billingInterval = "monthly";
  const planCode = "ams_standard";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      setIsLoading(false);
      return;
    }

    if (!formData.agreeToTerms) {
      setError("You must agree to the Terms & Conditions");
      setIsLoading(false);
      return;
    }

    try {
      console.log("[SignUpPage] Calling signup Edge Function...");

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const referralCode = sessionStorage.getItem("referral_code");
      if (referralCode) {
        console.log("[SignUpPage] Using referral code:", referralCode);
      }

      const response = await fetch(
        `${supabaseUrl}/functions/v1/auth_signup-with-plan`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: anonKey,
            Authorization: `Bearer ${anonKey}`,
          },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            first_name: formData.firstName,
            last_name: formData.lastName,
            company_name: formData.companyName,
            selected_plan_code: planCode,
            billing_interval: billingInterval,
            referral_code: referralCode || undefined,
          }),
        }
      );

      const data = await response.json();

      console.log("[SignUpPage] Response:", data);

      if (!response.ok || !data.ok) {
        throw new Error(data.error || data.message || "Signup failed");
      }

      const result = data.data;

      if (!result.success) {
        throw new Error(result.error || "Signup failed");
      }

      // CRITICAL: Establish session BEFORE redirecting anywhere
      if (result.access_token && result.refresh_token) {
        console.log("[SignUpPage] Establishing session with tokens...");
        
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: result.access_token,
          refresh_token: result.refresh_token
        });
        
        if (sessionError) {
          console.error("[SignUpPage] Failed to set session:", sessionError);
          throw new Error("Failed to establish session");
        }
        
        console.log("[SignUpPage] Session established successfully!");
      }

      // Handle success - now user is logged in
      if (result.requires_payment) {
        console.log("[SignUpPage] Redirecting to Stripe checkout...");
        if (result.checkout_url) {
          window.location.href = result.checkout_url;
        } else {
          throw new Error("No checkout URL returned");
        }
      } else {
        console.log("[SignUpPage] Free plan - redirecting to welcome page...");
        window.location.href = "/fma-welcome";
      }
    } catch (err) {
      console.error("[SignUpPage] Error:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again."
      );
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Client Management",
      description: "Track clients, policies, and interactions in one central hub"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Agent Recruiting",
      description: "Streamline your recruiting process and onboard new agents efficiently"
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Task Manager",
      description: "Never miss a follow-up with intelligent task automation"
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email Marketing",
      description: "Built-in email campaigns and automated drip sequences"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Sales Analytics",
      description: "Real-time insights into your agency's performance"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Pipeline Automation",
      description: "Automate your sales process and close more deals"
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Agency Management",
      description: "Complete control over your entire agency operations"
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Document Storage",
      description: "Securely store and organize all agency documents"
    },
  ];

  const benefits = [
    "Google Integrations",
    "Full AMS + Basic FMA Access",
    "Team invites | Collaboration",
    "Power Dialer Already Built-In",
    "Training & Onboarding Included",
    "Cancel Anytime. No Nonsense.",
  ];

  return (
    <>
      {/* Animated background with shooting stars */}
      <div className="fixed inset-0 bg-neutral-900 z-0"></div>
      <StarsBackground
        starDensity={0.0003}
        className="fixed inset-0 z-10"
      />
      <ShootingStars
        minSpeed={5}
        maxSpeed={30}
        minDelay={800}
        maxDelay={3000}
        starColor="#ff6b35"
        trailColor="#9E00FF"
        starWidth={15}
        starHeight={2}
        className="fixed inset-0 z-20"
      />

      <div className="min-h-screen p-4 py-12 relative z-30">

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 inline-flex items-center justify-center mx-auto"
          >
            <img
              src="/logos/Orca-Software-orange-white-Logo.png"
              alt="Orca Software"
              className="h-48 w-auto"
            />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 px-4 leading-tight"
          >
            <span className="bg-gradient-to-r from-orange-400 to-orange-500 bg-clip-text text-transparent">
              Agency Management Software
            </span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl md:text-2xl text-white/70 mb-4"
          >
            More Than a CRM
          </motion.p>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-lg text-white/60 max-w-3xl mx-auto"
          >
            Complete task management, recruiting automation, client tracking, and email marketing - all in one powerful platform
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Features Grid */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Card 
              className="border-orange-500/20 h-full"
              style={{
                background: "rgba(11, 17, 32, 0.8)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 107, 53, 0.1)",
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 via-transparent to-orange-500/20 pointer-events-none rounded-lg"></div>
              
              <CardHeader className="relative">
                <CardTitle className="text-2xl text-white flex items-center gap-2">
                  <Zap className="w-7 h-7 text-orange-400" />
                  Everything Your Agency Needs
                </CardTitle>
                <CardDescription className="text-white/60 text-base">
                  Built specifically for insurance agencies and agents to scale and succeed
                </CardDescription>
              </CardHeader>
              
              <CardContent className="relative">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {features.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.5 + index * 0.05 }}
                      className="flex items-start gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <div className="text-orange-400 flex-shrink-0 mt-1">
                        {feature.icon}
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-sm mb-1">
                          {feature.title}
                        </h3>
                        <p className="text-white/60 text-xs">
                          {feature.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Benefits List */}
                <div className="pt-6 border-t border-white/10">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-orange-400" />
                    What's Included
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {benefits.map((benefit, index) => (
                      <div key={index} className="flex items-center gap-2 text-white/70 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-orange-400 flex-shrink-0" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pricing */}
                <div className="mt-6 p-4 rounded-lg bg-gradient-to-r from-orange-500/20 to-orange-600/10 border border-orange-500/30">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl font-bold text-white">$59</span>
                    <span className="text-white/60">/month</span>
                    <span className="text-sm text-orange-400 ml-auto">or $500/year (Save $208)</span>
                  </div>
                  <p className="text-white/70 text-sm">
                    Free basic Find My Agency access included with every AMS subscription.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Signup Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Card 
              className="border-orange-500/20 h-full"
              style={{
                background: "rgba(11, 17, 32, 0.8)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 107, 53, 0.1)",
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 via-transparent to-orange-500/20 pointer-events-none rounded-lg"></div>
              
              <CardHeader className="relative">
                <CardTitle className="text-2xl text-white">
                  Start Your Journey.
                </CardTitle>
                <CardDescription className="text-white/60">
                  Get started in minutes. Save hours. 
                </CardDescription>
              </CardHeader>
              
              <CardContent className="relative">
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-orange-400 font-medium">
                        First Name *
                      </Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                        className="bg-background/30 border-border/50 text-white placeholder:text-white/60 focus:border-orange-500/50 focus:ring-orange-500/20 h-11"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-orange-400 font-medium">
                        Last Name *
                      </Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                        className="bg-background/30 border-border/50 text-white placeholder:text-white/60 focus:border-orange-500/50 focus:ring-orange-500/20 h-11"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="companyName" className="text-orange-400 font-medium">
                      Agency Name *
                    </Label>
                    <Input
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="Your Insurance Agency LLC"
                      required
                      disabled={isLoading}
                      className="bg-background/30 border-border/50 text-white placeholder:text-white/60 focus:border-orange-500/50 focus:ring-orange-500/20 h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-orange-400 font-medium">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@agency.com"
                      required
                      disabled={isLoading}
                      className="bg-background/30 border-border/50 text-white placeholder:text-white/60 focus:border-orange-500/50 focus:ring-orange-500/20 h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-orange-400 font-medium">
                      Password *
                    </Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="At least 8 characters"
                      required
                      disabled={isLoading}
                      className="bg-background/30 border-border/50 text-white placeholder:text-white/60 focus:border-orange-500/50 focus:ring-orange-500/20 h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-orange-400 font-medium">
                      Confirm Password *
                    </Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Re-enter password"
                      required
                      disabled={isLoading}
                      className="bg-background/30 border-border/50 text-white placeholder:text-white/60 focus:border-orange-500/50 focus:ring-orange-500/20 h-11"
                    />
                  </div>

                  <div className="flex items-start space-x-3 pt-2">
                    <input
                      type="checkbox"
                      id="agreeToTerms"
                      name="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={handleChange}
                      disabled={isLoading}
                      className="mt-1 h-4 w-4 rounded border-white/20 bg-white/10 text-orange-500 focus:ring-orange-500 focus:ring-offset-0 cursor-pointer"
                    />
                    <label
                      htmlFor="agreeToTerms"
                      className="text-sm text-gray-300 cursor-pointer"
                    >
                      I agree to the{" "}
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-400 hover:text-orange-300 underline"
                      >
                        Terms & Conditions
                      </a>
                    </label>
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-12 text-base font-semibold bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 border-0 shadow-lg shadow-orange-500/25 transition-all duration-300"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Zap className="w-5 h-5 mr-2" />
                        Start Winning - Continue to Payment
                      </>
                    )}
                  </Button>

                  <div className="space-y-2 pt-2">
                    <p className="text-center text-sm text-white/50">
                      Already have an account?{" "}
                      <a href="/login" className="text-orange-400 hover:text-orange-300 font-medium transition-colors">
                        Sign in
                      </a>
                    </p>
                    <p className="text-center text-xs text-white/40">
                      Secure payment powered by Stripe
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center text-white/50 text-sm"
        >
          <p>
            Need help?{" "}
            <a
              href="mailto:Support@findmyagency.net"
              className="text-orange-400 hover:text-orange-300 transition-colors inline-flex items-center gap-1"
            >
              <Mail className="w-4 h-4" />
              Contact Support
            </a>
          </p>
        </motion.div>
      </div>
    </div>
    </>
  );
}
